import React, {
  useState,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';

import {
  Box,
  Button,
  Grid,
  LinearProgress,
  Tooltip,
  Typography,
} from '@material-ui/core';

import { NewCentralFormType, SearchResult, SpecificFilterRow } from '../types';
import {
  HandlePagingParams,
  InitialResultDto,
  ParamsMap,
  SearchEntityEnum,
} from './search-v3-types';
import { searchV3Paging } from './search-v3-api';
import { FormikProvider, useFormik } from 'formik';
import SearchIcon from '@material-ui/icons/Search';
import SpecificFilterRow2, {
  getInitialSpecificFilterRowVals,
} from '../v2/specificFilterRows/filter-row';

import { SearchDictionaryProvider } from '../context/dictionary-context';

import {
  getInitialSpecificFilterRowValsV3,
  INIT_VALS_V3,
} from './search-v3-initial-values';

import {
  getInitialResultActiveTab,
  getSearchSpaceIntersection,
} from './search-v3-utils';
import { SearchV3InitalResults } from './search-v3-results';
import Pagination from '@material-ui/lab/Pagination';
import { usePagination } from './search-v3-pagination';

import { submitInitialSearchFormV3 } from './search-v3-submit-form';
import { NotificationCtx } from 'components/notification/notification-hook';
import SearchEntityTypesFilter from './search-v3-entity-types-filter';
import EvidenceUnitsFilter from '../v2/evidence-units-filter/evidence-units-filter';
import SearchFiltersArchivalUnits from '../filters/search-filter-archival-units';
import { useStyles } from './search-v3-styles';
import { useSearchContext } from '../context/search-context';
import SearchV3CollectionsAndAidsSelects from './search-v3-collections-and-aids-selects';
import { SearchNoResults } from './search-v3-no-results';
import { SearchSummary } from './search-v3-summary-panel';

const SearchButtons = ({
  handleResetForm,
  handleResetFormValues,
}: {
  handleResetForm: () => void;
  handleResetFormValues: () => void;
}) => (
  <Grid item xs={12} style={{ margin: '16px 0' }}>
    <Box display="flex" justifyContent="center">
      <Button type="submit" variant="contained" color="primary">
        <SearchIcon style={{ marginRight: 8 }} />
        <Typography component="span" variant="body2">
          HLEDAT
        </Typography>
      </Button>
      <Button
        style={{ marginLeft: 16 }}
        variant="outlined"
        onClick={(e) => {
          e.preventDefault();
          handleResetForm();
        }}
      >
        Resetovat
      </Button>
      <Button
        style={{ marginLeft: 16 }}
        variant="outlined"
        onClick={(e) => {
          e.preventDefault();
          handleResetFormValues();
        }}
      >
        Resetovat hodnoty
      </Button>
    </Box>
  </Grid>
);

function SearchV3() {
  const [paramsMap, setParamMap] = useState<ParamsMap>({});
  const [initialResult, setInitialResult] = useState<
    InitialResultDto | undefined
  >();

  const [pagingResult, setPagingResult] = useState<SearchResult>();
  const [selectedEntityType, setSelectedEntityType] = useState<
    SearchEntityEnum
  >(SearchEntityEnum.ARCHIVAL_COLLECTION);

  const resultsDivRef = useRef<HTMLDivElement>(null);

  const { showNotification } = useContext(NotificationCtx);
  const searchContext = useSearchContext();

  const { setResetForm, setCurrentFormValues } = searchContext;

  const {
    updatePage,
    updateCount,
    pageCount,
    page,
    pageSize,
  } = usePagination();

  ///////////////////////////////////////////////////////////////////////////

  const scrollToResults = useCallback(() => {
    const element = resultsDivRef.current;
    const stickyHeaderOffset = 81;

    if (element) {
      // Calculate the target scroll position
      const elementRect = element.getBoundingClientRect();
      const absoluteElementTop = elementRect.top + window.scrollY;
      const targetScrollPosition = absoluteElementTop - stickyHeaderOffset * 2;

      window.scrollTo({ top: targetScrollPosition, behavior: 'smooth' });
    }
  }, []);

  const [loading, setLoading] = React.useState(false);
  const formikContext = useFormik<NewCentralFormType>({
    initialValues: INIT_VALS_V3,
    onSubmit: submitInitialSearchFormV3(
      setInitialResult,
      showNotification,
      async (initialBody, initialResult) => {
        searchContext.setCurrentFormValues?.(values);
        if (initialResult) {
          const relevantEntityType = getInitialResultActiveTab(
            initialResult,
            selectedEntityType
          );
          if (!relevantEntityType) {
            setPagingResult(undefined);
            return;
          }
          setParamMap(initialBody.paramsMap);
          if (selectedEntityType !== relevantEntityType) {
            setSelectedEntityType(relevantEntityType);
          }
          updatePage(1);
          await handlePaging({
            paramsMap: initialBody.paramsMap,
            archivalCollectionIds:
              initialResult?.resultMap?.ARCHIVAL_COLLECTION?.ids ?? [],
            archivalAidIds: initialResult?.resultMap?.ARCHIVAL_AID?.ids ?? [],
            selectedEntityType: relevantEntityType,
            page: 1,
            pageSize,
          });
          setTimeout(() => {
            scrollToResults();
          }, 100);
        }
      }
    ),
    onReset: (values) => {
      if (!values) {
        setInitialResult(undefined);
        setPagingResult(undefined);
      }
    },
  });

  const { values, setFieldValue, resetForm } = formikContext;

  const handleRowAdd = useCallback(() => {
    setFieldValue('specificFilterRows', [
      ...values.specificFilterRows,
      getInitialSpecificFilterRowValsV3(),
    ]);
  }, [setFieldValue, values.specificFilterRows]);

  const handleRowRemove = useCallback(
    (rowIndex) => {
      const newRows = values.specificFilterRows.filter(
        (_, i) => i !== rowIndex
      );
      setFieldValue('specificFilterRows', newRows);
      searchContext.setValidSearchSpace?.(
        getSearchSpaceIntersection(newRows.map((r) => r.filterOption))
      );
    },
    [values.specificFilterRows, setFieldValue, searchContext]
  );

  useEffect(() => {
    updateCount(pagingResult?.count ?? 0);
  }, [pagingResult?.count, updateCount]);

  useEffect(() => {
    setResetForm?.({ current: resetForm });
  }, [resetForm, setResetForm]);

  useEffect(() => {
    setCurrentFormValues?.(values);
  }, [values, setCurrentFormValues]);

  // const handlePageSizeChange = useCallback(
  //   (pageSizeStr: string) => {
  //     const pageSize = parseInt(pageSizeStr);

  //     if (isNaN(pageSize)) {
  //       return;
  //     }
  //     setFieldValue('pageSize', pageSize);
  //     setFieldValue('page', 1);
  //     submitForm();
  //   },
  //   [setFieldValue, submitForm]
  // );

  ///////////////////////////////////////////////////////////////////////////

  const handlePaging = useCallback(
    async (params: HandlePagingParams) => {
      setLoading(true);
      const json = await searchV3Paging({
        paramsMap: params.paramsMap,
        size: params.pageSize ?? pageSize,
        offset: Math.max(0, (params.page - 1) * (params.pageSize ?? pageSize)),
        selectedEntityType: params.selectedEntityType,
        archivalCollectionIds:
          params.archivalCollectionIds ??
          initialResult?.resultMap?.ARCHIVAL_COLLECTION?.ids ??
          [],
        archivalAidIds:
          params.archivalAidIds ??
          initialResult?.resultMap.ARCHIVAL_AID?.ids ??
          [],
      });
      setPagingResult(json);
      setLoading(false);
    },
    [
      initialResult?.resultMap.ARCHIVAL_AID?.ids,
      initialResult?.resultMap?.ARCHIVAL_COLLECTION?.ids,
      pageSize,
    ]
  );

  const handlePageChange = useCallback(
    async (_: unknown, newPage: number) => {
      await handlePaging({
        paramsMap,
        selectedEntityType,
        page: newPage,
        pageSize,
        archivalAidIds: initialResult?.resultMap.ARCHIVAL_AID?.ids ?? [],
        archivalCollectionIds:
          initialResult?.resultMap.ARCHIVAL_COLLECTION?.ids ?? [],
      });
      updatePage(newPage);
    },
    [
      handlePaging,
      initialResult?.resultMap.ARCHIVAL_AID?.ids,
      initialResult?.resultMap.ARCHIVAL_COLLECTION?.ids,
      pageSize,
      paramsMap,
      selectedEntityType,
      updatePage,
    ]
  );

  const handleResetForm = useCallback(() => {
    resetForm({ values: INIT_VALS_V3 });
    searchContext.setValidSearchSpace?.([
      SearchEntityEnum.ARCHIVAL_AID,
      SearchEntityEnum.ARCHIVAL_COLLECTION,
      SearchEntityEnum.EAD_NODE_UNIT,
      SearchEntityEnum.ORIGINATOR,
    ]);
    setInitialResult(undefined);
    setPagingResult(undefined);
  }, [resetForm, searchContext]);

  const handleResetFormValues = useCallback(() => {
    const emptyRows: SpecificFilterRow[] = values.specificFilterRows.map(
      (row) => ({
        ...row,
        ...getInitialSpecificFilterRowVals(row.filterOption),
      })
    );
    resetForm({
      values: {
        ...values,
        specificFilterRows: emptyRows,
      },
    });
    setInitialResult(undefined);
    setPagingResult(undefined);
  }, [resetForm, values]);

  const classes = useStyles();

  return (
    <SearchDictionaryProvider>
      <FormikProvider value={formikContext}>
        <form onSubmit={formikContext.handleSubmit}>
          <SearchSummary pageCount={pageCount} />
          <Grid container style={{ justifyContent: 'center' }}>
            <Grid item xs={12} md={10} className={classes.body}>
              <SearchButtons
                handleResetForm={handleResetForm}
                handleResetFormValues={handleResetFormValues}
              />
              {(loading || formikContext.isSubmitting) && <LinearProgress />}
              <Grid container alignItems="center">
                {values.specificFilterRows.map((_, index) => (
                  <SpecificFilterRow2
                    key={`row-${index}`}
                    onRowRemove={handleRowRemove}
                    rowIndex={index}
                  />
                ))}
              </Grid>

              <Grid item xs={12}>
                <Box
                  display="flex"
                  justifyContent="flex-end"
                  marginTop={1}
                  marginBottom={2}
                >
                  <Tooltip
                    title={<span style={{ fontSize: 16 }}>Přidat filtr</span>}
                    placement="top"
                  >
                    <Button
                      style={{ marginRight: 8 }}
                      onClick={handleRowAdd}
                      variant="contained"
                      color="primary"
                    >
                      <Typography component="span" variant="h4">
                        +
                      </Typography>
                      <Typography component="span" style={{ marginLeft: 8 }}>
                        Přidat filtr
                      </Typography>
                    </Button>
                  </Tooltip>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <SearchFiltersArchivalUnits />
              </Grid>

              <Grid item xs={12} style={{ marginTop: 16 }}>
                <EvidenceUnitsFilter />
              </Grid>
              <Grid item xs={12} style={{ marginTop: 16 }}>
                <SearchV3CollectionsAndAidsSelects />
              </Grid>
              <Grid
                item
                xs={12}
                style={{
                  marginTop: 16,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <SearchEntityTypesFilter />
              </Grid>
              <Grid item xs={12} style={{ marginTop: 16 }}>
                <SearchButtons
                  handleResetForm={handleResetForm}
                  handleResetFormValues={handleResetFormValues}
                />
              </Grid>

              <Box display="flex" justifyContent="center" width="100%" my={2} />
              {loading || formikContext.isSubmitting ? (
                <LinearProgress />
              ) : (
                <hr />
              )}
              <div ref={resultsDivRef} />
              {pageCount > 0 && !formikContext.isSubmitting ? (
                <>
                  <Box display="flex" justifyContent="center" mt={4}>
                    Výsledky hledání:{' '}
                  </Box>

                  <Box
                    display="flex"
                    justifyContent="center"
                    marginTop={4}
                    marginBottom={4}
                  >
                    <Pagination
                      count={pageCount}
                      page={page}
                      onChange={handlePageChange}
                    />
                  </Box>

                  <SearchV3InitalResults
                    key={`res-${page}`}
                    initialResult={initialResult}
                    loading={formikContext.isSubmitting}
                    setLoading={setLoading}
                    setSelectedEntityType={setSelectedEntityType}
                    handlePaging={handlePaging}
                    selectedEntityType={selectedEntityType}
                    pagingResult={pagingResult}
                    paramsMap={paramsMap}
                    pagingLoading={loading}
                  />

                  <Box
                    display="flex"
                    justifyContent="center"
                    marginTop={4}
                    marginBottom={4}
                  >
                    <Pagination
                      count={pageCount}
                      page={page}
                      onChange={handlePageChange}
                    />
                  </Box>
                </>
              ) : (
                <SearchNoResults />
              )}
            </Grid>
          </Grid>
        </form>
      </FormikProvider>
    </SearchDictionaryProvider>
  );
}

export default SearchV3;
